import React, { useState } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaExpand } from 'react-icons/fa';
import './ImageGallery.css';

const ImageGallery = ({ images = [], layout = 'grid' }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') goToPrevious(e);
    if (e.key === 'ArrowRight') goToNext(e);
  };

  // Single image layout
  if (images.length === 1) {
    return (
      <>
        <div className="single-image-container" onClick={() => openLightbox(0)}>
          <img 
            src={images[0].url} 
            alt={images[0].alt || 'Foto artikel'} 
            className="single-image"
          />
          <div className="image-expand-hint">
            <FaExpand />
            <span>Klik untuk memperbesar</span>
          </div>
        </div>

        {lightboxOpen && (
          <Lightbox
            images={images}
            currentIndex={currentIndex}
            onClose={closeLightbox}
            onPrevious={goToPrevious}
            onNext={goToNext}
            onKeyDown={handleKeyDown}
          />
        )}
      </>
    );
  }

  // Multiple images - grid layout
  return (
    <>
      <div className={`image-gallery gallery-${images.length}`}>
        {images.map((image, index) => (
          <div 
            key={index} 
            className={`gallery-item item-${index + 1}`}
            onClick={() => openLightbox(index)}
          >
            <img 
              src={image.url} 
              alt={image.alt || `Foto ${index + 1}`} 
            />
            <div className="gallery-item-overlay">
              <FaExpand />
            </div>
          </div>
        ))}
      </div>

      {lightboxOpen && (
        <Lightbox
          images={images}
          currentIndex={currentIndex}
          onClose={closeLightbox}
          onPrevious={goToPrevious}
          onNext={goToNext}
          onKeyDown={handleKeyDown}
        />
      )}
    </>
  );
};

// Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onPrevious, onNext, onKeyDown }) => {
  React.useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  const currentImage = images[currentIndex];

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose}>
        <FaTimes />
      </button>

      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        {images.length > 1 && (
          <button className="lightbox-nav lightbox-prev" onClick={onPrevious}>
            <FaChevronLeft />
          </button>
        )}

        <div className="lightbox-image-container">
          <img 
            src={currentImage.url} 
            alt={currentImage.alt || 'Foto'} 
            className="lightbox-image"
          />
          {currentImage.alt && (
            <p className="lightbox-caption">{currentImage.alt}</p>
          )}
        </div>

        {images.length > 1 && (
          <button className="lightbox-nav lightbox-next" onClick={onNext}>
            <FaChevronRight />
          </button>
        )}
      </div>

      {images.length > 1 && (
        <div className="lightbox-counter">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="lightbox-thumbnails">
          {images.map((image, index) => (
            <button
              key={index}
              className={`thumbnail-btn ${index === currentIndex ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                // setCurrentIndex would need to be passed or handled differently
              }}
            >
              <img src={image.url} alt="" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
